<template>
  <component
    :is="containerComponent"
    v-if="visible"
    v-bind="containerProps"
    data-testid="time-off-view-modal"
    @cancel="closeModal"
  >
    <modal-navigation
      v-if="timeOffList.length && currentIndex !== -1"
      :current="currentIndex"
      :disabled="loading"
      :total="timeOffList.length"
      test-id="time-off-view-navigation"
      previous-button-test-id="time-off-view-previous-button"
      next-button-test-id="time-off-view-next-button"
      @navigate="navigate"
    />

    <div v-if="loading" class="loading-spin" data-testid="time-off-view-loading">
      <a-spin />
    </div>
    <div v-else class="flex flex-column gap-3" data-testid="time-off-view-content">
      <template v-if="embedded">
        <h3 class="m-0">Time Off Request</h3>
        <p class="m-0">
          Review the requested days, schedule conflicts, and payment details before taking an available action.
        </p>
      </template>
      <time-off-info data-testid="time-off-view-info" />
      <time-off-scheduler
        ref="schedulerRef"
        :loading-approve.sync="loadingApprove"
        :loading-decline.sync="loadingDecline"
        :checked.sync="checked"
        data-testid="time-off-view-scheduler"
        @delete="ids => handleDeleteDays(ids)"
        @approve="handleReload"
      />
      <time-off-payment
        ref="paymentRef"
        :loading.sync="loadingApprove"
        :checked.sync="checked"
        data-testid="time-off-view-payment"
        @delete="ids => handleDeleteDays(ids)"
        @approve="handleReload"
        @selection-change="selectedPaymentCount = $event"
      />
    </div>
    <div v-if="embedded" class="time-off-view-footer" data-testid="time-off-view-footer">
      <div class="flex justify-content-end">
        <div class="flex gap-2" v-if="timeOff">
          <a-button
            type="danger"
            @click="handleDelete"
            :loading="loadingDelete"
            data-testid="time-off-view-delete-button"
          >
            Delete Time Off
          </a-button>
          <div
            class="flex gap-2"
            data-testid="time-off-view-scheduler-actions"
            v-if="
              timeOff.status === 1 &&
              hasUncontrolledPermission('manage_shifts', {
                id: timeOff.associate.id
              })
            "
          >
            <a-button
              type="danger"
              @click="handleApprove('scheduler', false)"
              :loading="loadingDecline"
              :disabled="!checked"
              data-testid="time-off-view-decline-button"
              >Decline</a-button
            ><a-button
              type="primary"
              @click="handleApprove('scheduler', true)"
              :loading="loadingApprove"
              :disabled="!checked || !$refs.schedulerRef.dates.length"
              data-testid="time-off-view-approve-button"
              >Approve</a-button
            >
          </div>
          <a-button
            type="primary"
            @click="handleApprove('payment', true)"
            :loading="loadingApprove"
            :disabled="!checked"
            data-testid="time-off-view-mark-payable-button"
            v-else-if="
              timeOff?.status === 2 &&
              hasAnyUncontrolledPermission(['validate_payments', 'process_payments'], {
                id: timeOff.associate.id
              })
            "
            >{{ selectedPaymentCount ? 'Mark Selected as Payable' : 'Leave Approved Days Unpaid' }}</a-button
          >
        </div>
      </div>
    </div>
    <template v-else slot="footer">
      <div class="flex justify-content-between" data-testid="time-off-view-footer">
        <a-button @click="closeModal" data-testid="time-off-view-close-button">Close</a-button>
        <div class="flex gap-2" v-if="timeOff">
          <a-button
            type="danger"
            @click="handleDelete"
            :loading="loadingDelete"
            data-testid="time-off-view-delete-button"
          >
            Delete Time Off
          </a-button>
          <div
            class="flex gap-2"
            data-testid="time-off-view-scheduler-actions"
            v-if="
              timeOff.status === 1 &&
              hasUncontrolledPermission('manage_shifts', {
                id: timeOff.associate.id
              })
            "
          >
            <a-button
              type="danger"
              @click="handleApprove('scheduler', false)"
              :loading="loadingDecline"
              :disabled="!checked"
              data-testid="time-off-view-decline-button"
              >Decline</a-button
            ><a-button
              type="primary"
              @click="handleApprove('scheduler', true)"
              :loading="loadingApprove"
              :disabled="!checked || !$refs.schedulerRef.dates.length"
              data-testid="time-off-view-approve-button"
              >Approve</a-button
            >
          </div>
          <a-button
            type="primary"
            @click="handleApprove('payment', true)"
            :loading="loadingApprove"
            :disabled="!checked"
            data-testid="time-off-view-mark-payable-button"
            v-else-if="
              timeOff?.status === 2 &&
              hasAnyUncontrolledPermission(['validate_payments', 'process_payments'], {
                id: timeOff.associate.id
              })
            "
            >{{ selectedPaymentCount ? 'Mark Selected as Payable' : 'Leave Approved Days Unpaid' }}</a-button
          >
        </div>
      </div>
    </template>
  </component>
</template>

<script>
import permissionMixin from '@/mixins/permissionMixin'
import { Button, Modal, notification, Spin } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import TimeOffViewInfo from './time-off-view-info.vue'
import TimeOffViewScheduler from './time-off-view-scheduler.vue'
import TimeOffViewPayment from './time-off-view-payment.vue'
import ModalNavigation from '@/components/entities/modal/modal-navigation.vue'

const { mapActions, mapState, mapGetters } = createNamespacedHelpers('timeOff')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-modal': Modal,
    'a-button': Button,
    'a-spin': Spin,
    'time-off-info': TimeOffViewInfo,
    'time-off-scheduler': TimeOffViewScheduler,
    'time-off-payment': TimeOffViewPayment,
    'modal-navigation': ModalNavigation
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    timeOffId: {
      type: Number,
      default: undefined
    },
    timeOffList: {
      type: Array,
      default: () => []
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      loadingApprove: false,
      loadingDecline: false,
      loadingDelete: false,
      checked: false,
      selectedPaymentCount: 0,

      currentIndex: -1
    }
  },
  mixins: [permissionMixin],
  watch: {
    visible: {
      handler(newVal) {
        if (newVal && this.timeOffId) {
          this.currentIndex = this.timeOffList.findIndex(item => item === this.timeOffId)
          this.fetchTimeOff()
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters(['getTimeOffStatus', 'getTimeOffType', 'getTimeOffRequestType']),

    ...mapState({
      timeOff: state => state.timeOff
    }),

    title() {
      return this.timeOff ? `Time Off Approval №${this.timeOff.id}` : 'Time Off Approval'
    },

    containerComponent() {
      return this.embedded ? 'div' : 'a-modal'
    },

    containerProps() {
      if (this.embedded) {
        return {
          class: 'time-off-view-embedded'
        }
      }

      return {
        title: this.title,
        visible: this.visible,
        destroyOnClose: true,
        width: '700px'
      }
    }
  },
  methods: {
    ...mapActions(['getTimeOff', 'getTimeOffStatistic', 'deleteTimeOff', 'deleteDaysTimeOff']),
    ...modalActions(['openModal']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    closeModal() {
      this.$emit('update:visible', false)
      this.$emit('update:timeOffId', undefined)
    },

    handleReload() {
      this.fetchTimeOff()
      this.$emit('update')
    },

    async navigate(index) {
      this.currentIndex = index

      this.$emit('update:timeOffId', this.timeOffList[index])

      this.$nextTick(async () => {
        await this.fetchTimeOff()
      })
    },

    async fetchTimeOff() {
      try {
        this.loading = true
        this.selectedPaymentCount = 0

        const payload = {
          id: this.timeOffId
        }

        await this.getTimeOff(payload)

        await this.getTimeOffStatistic({
          associate_id: this.timeOff.associate.id,
          type: this.timeOff.type
        })
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        this.closeModal()
      } finally {
        this.loading = false
      }
    },

    handleApprove(type, status) {
      if (type === 'scheduler') {
        this.$refs.schedulerRef.handleApprove(status)
      } else {
        this.$refs.paymentRef.handleApprove(status)
      }
    },

    handleDeleteDays(ids) {
      this.openModal({
        type: 'deletion',
        data: {
          visible: true,
          title: 'Delete Time Off Days',
          description: 'Are you sure you want to delete this days?',
          onConfirm: async () => {
            await this.deleteDaysTimeOff({ ids })

            if (this.timeOff.items.length === ids.length) {
              this.$emit('update')
              this.closeModal()
            } else {
              this.handleReload()
            }

            this.showNotification('success', 'Info', 'Time off days have been deleted successfully')
          }
        }
      })
    },

    handleDelete() {
      this.openModal({
        type: 'deletion',
        data: {
          visible: true,
          title: 'Delete Time Off',
          description: 'Are you sure you want to delete this time off?',
          onConfirm: async () => {
            await this.deleteTimeOff({ id: this.timeOffId })

            this.$emit('update')
            this.closeModal()
            this.showNotification('success', 'Info', 'Time off has been deleted successfully')
          }
        }
      })
    }
  }
}
</script>

<style scoped>
::v-deep .ant-modal-body {
  max-height: 800px;
  overflow: auto;
}

.time-off-view-embedded {
  max-height: 650px;
  overflow: auto;
}

.time-off-view-footer {
  margin-top: 16px;
}
</style>
